/**
 * Build the diagonal-stripe buffer overlay for the self-schedule
 * calendar as a set of **merged, whole-group** zones.
 *
 * `before + slot + after` is treated as ONE clearance group. Each slot
 * contributes a single band spanning `[start - before, end + after]`,
 * and overlapping / touching bands are merged so a run of slots reads
 * as one continuous diagonal zone — never a stack of seams (SCH-58
 * bug 1) and never fragmented (SCH-58 bug 2).
 *
 * The overlay is deliberately NOT clipped against slots or busy events.
 * Picked slots and busy blocks are foreground events; the buffer is a
 * `display: background` event sitting behind them (z-index 2), so they
 * paint on top naturally. Clipping the band around them is what made
 * the buffer look split across a busy block — which defeats the point
 * of the feature (clearance before/after the meeting). The generator
 * already keeps generated slots' buffers clear of all busy (interviewer
 * + meeting room) by reserving clearance inside each busy-free gap; the
 * only time a band sits over a busy block is a multi-interviewer 1-on-1
 * union pick (a slot offered because ONE interviewer is free, sitting
 * across ANOTHER's busy), and there the continuous band is correct.
 *
 * Pure (no React / FullCalendar) so it's unit-testable. Rendering-only:
 * does not touch slot generation or the wire payload.
 */
import { mergeIntervals, type Interval } from '../../../availability';
import moment from 'moment-timezone';

export interface BuildBufferZonesArgs {
  /** Active picked slots (start/end ISO). */
  slots: Interval[];
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  timezone: string;
}

/**
 * Returns the buffer overlay zones as ISO intervals. Empty when both
 * buffer sides are zero or there are no slots.
 */
export function buildBufferZones({
  slots,
  bufferBeforeMinutes,
  bufferAfterMinutes,
  timezone,
}: BuildBufferZonesArgs): Interval[] {
  const beforeMs = Math.max(0, bufferBeforeMinutes) * 60_000;
  const afterMs = Math.max(0, bufferAfterMinutes) * 60_000;
  if (beforeMs <= 0 && afterMs <= 0) return [];
  if (slots.length === 0) return [];

  // One band per slot covering the whole group: before clearance, the
  // slot itself (painted over by the foreground pill), and after
  // clearance. Merging then connects adjacent slots into one zone.
  const raw: Interval[] = [];
  for (const s of slots) {
    const startMs = moment.tz(s.start, timezone).valueOf();
    const endMs = moment.tz(s.end, timezone).valueOf();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
    raw.push({
      start: moment.tz(startMs - beforeMs, timezone).toISOString(),
      end: moment.tz(endMs + afterMs, timezone).toISOString(),
    });
  }
  if (raw.length === 0) return [];

  return mergeIntervals(raw, timezone);
}
